import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";

interface SceneGrassProps {
	enabled: boolean;
	/** Total instance count to scatter around the sauna. */
	density: number;
	/**
	 * Sauna + deck footprint to keep clear of grass. World-aligned
	 * AABB centred on the origin (the engines' world origin sits at
	 * the sauna centre).
	 */
	footprint: { width: number; length: number };
	/** Ground Y at the centre of the scene. Blades root here. */
	groundY: number;
	/**
	 * Outer radius (m) of the grass disc around the sauna. Blades
	 * stop here so they don't fight the edge-fade in the ground
	 * shader at ~14 m.
	 */
	radius?: number;
	/**
	 * Wind-shader sway amplitude (world m on the blade tip). The
	 * default is small enough that even at full uTime gain the tip
	 * stays inside its instance footprint, so neighbouring blades
	 * don't visibly intersect.
	 */
	swayAmplitude?: number;
}

const BLADE_HEIGHT = 0.13;
const BLADE_HALFWIDTH = 0.013;
const DEFAULT_RADIUS_M = 8;
const DEFAULT_SWAY_M = 0.025;
const FOOTPRINT_PADDING_M = 0.4;

/**
 * Hash-based pseudo-random in [0, 1). Deterministic — same density
 * always produces the same scatter, so the placement is stable
 * across reloads (and across tests).
 */
function hash01(seed: number, salt: number): number {
	const x = Math.sin(seed * 92.81 + salt * 47.357) * 43758.5453;
	return x - Math.floor(x);
}

function createBladeGeometry(): THREE.BufferGeometry {
	// 1-triangle blade: two base verts, one tip. Single triangle
	// keeps vertex cost minimal; the wind shader bends only the tip.
	const geometry = new THREE.BufferGeometry();
	const positions = new Float32Array([
		-BLADE_HALFWIDTH,
		0,
		0,
		BLADE_HALFWIDTH,
		0,
		0,
		0,
		BLADE_HEIGHT,
		0,
	]);
	const normals = new Float32Array([0, 0, 1, 0, 0, 1, 0, 0, 1]);
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
	geometry.setIndex([0, 1, 2]);
	geometry.computeBoundingSphere();
	return geometry;
}

interface WindUniforms {
	uTime: { value: number };
	uSway: { value: number };
}

function createGrassMaterial(): {
	material: THREE.MeshStandardMaterial;
	uniformsRef: { current: WindUniforms | null };
} {
	const material = new THREE.MeshStandardMaterial({
		color: 0x6b7651,
		roughness: 0.92,
		metalness: 0,
		side: THREE.DoubleSide,
		envMapIntensity: 0.35,
	});
	const uniformsRef: { current: WindUniforms | null } = { current: null };

	material.onBeforeCompile = (shader) => {
		shader.uniforms.uTime = { value: 0 };
		shader.uniforms.uSway = { value: DEFAULT_SWAY_M };
		uniformsRef.current = {
			uTime: shader.uniforms.uTime as { value: number },
			uSway: shader.uniforms.uSway as { value: number },
		};

		shader.vertexShader = shader.vertexShader.replace(
			"#include <common>",
			`#include <common>
uniform float uTime;
uniform float uSway;
varying float vGrassTop;
varying float vGrassHueJitter;`,
		);

		shader.vertexShader = shader.vertexShader.replace(
			"#include <begin_vertex>",
			`#include <begin_vertex>
{
  // Bend amount peaks at the tip (y = BLADE_HEIGHT). Squared so the
  // base stays planted and only the upper third moves visibly.
  float bladeFrac = clamp(position.y / ${BLADE_HEIGHT.toFixed(4)}, 0.0, 1.0);
  float topness = bladeFrac * bladeFrac;

  // World-X of the instance origin so neighbouring blades sway out
  // of phase — reads as a wind front, not a synchronized wave.
  vec4 instanceOrigin = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
  float worldX = instanceOrigin.x;
  float worldZ = instanceOrigin.z;
  float phase = uTime * 0.6 + worldX * 0.42 + worldZ * 0.31;

  transformed.x += topness * sin(phase) * uSway;
  transformed.z += topness * cos(phase * 0.9) * uSway * 0.4;

  vGrassTop = bladeFrac;
  // Stable per-instance hue jitter so each blade reads as a slightly
  // different shade. fract(sin(...)) is a hash, not a uniform RNG.
  vGrassHueJitter = fract(sin(worldX * 12.9898 + worldZ * 78.233) * 43758.5453);
}`,
		);

		shader.fragmentShader = shader.fragmentShader.replace(
			"#include <common>",
			`#include <common>
varying float vGrassTop;
varying float vGrassHueJitter;`,
		);

		shader.fragmentShader = shader.fragmentShader.replace(
			"#include <color_fragment>",
			`#include <color_fragment>
{
  // Tip-to-base shading: tips lighter (caught light), base darker
  // (in shadow). Shifts the diffuse without going through a texture
  // lookup, which keeps this shader cheap.
  float baseShade = mix(0.78, 1.06, vGrassTop);
  float hueDrift = (vGrassHueJitter - 0.5) * 0.18;
  diffuseColor.rgb *= baseShade;
  diffuseColor.r += hueDrift * 0.04;
  diffuseColor.b -= hueDrift * 0.05;
  diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 1.0);
}`,
		);
	};
	material.needsUpdate = true;

	return { material, uniformsRef };
}

interface InstancePlacement {
	x: number;
	z: number;
	rotation: number;
	scale: number;
}

function placeInstances(
	density: number,
	radius: number,
	footprint: { width: number; length: number },
): InstancePlacement[] {
	const placements: InstancePlacement[] = [];
	const halfW = footprint.width / 2 + FOOTPRINT_PADDING_M;
	const halfL = footprint.length / 2 + FOOTPRINT_PADDING_M;

	// Sample more candidates than we need — cheaper than rejection
	// sampling against the footprint AABB getting expensive.
	const samples = density * 2;
	for (let i = 0; i < samples && placements.length < density; i += 1) {
		// Polar sampling, sqrt() so density is uniform over the disc.
		const r = Math.sqrt(hash01(i, 1.7)) * radius;
		const theta = hash01(i, 2.9) * Math.PI * 2;
		const x = Math.cos(theta) * r;
		const z = Math.sin(theta) * r;
		if (Math.abs(x) < halfW + 0.55 && Math.abs(z) < halfL + 0.55) continue;
		// Leave an open approach and cluster grass into patches instead of
		// distributing identical spikes over the entire lawn.
		if (z > 0 && Math.abs(x) < 1.05) continue;
		const patch = Math.sin(x * 1.6 + Math.sin(z * 0.9)) * Math.cos(z * 1.3);
		if (patch < -0.05) continue;

		placements.push({
			x,
			z,
			rotation: hash01(i, 3.7) * Math.PI * 2,
			scale: 0.7 + hash01(i, 4.1) * 0.5,
		});
	}
	return placements;
}

/**
 * High-tier-only instanced grass. The mesh is named
 * `dressing-grass` so it shares the dressing prefix used for
 * mesh-budget regression tests in viewer detailing.
 *
 * Animation is updated through `useFrame` but never calls
 * `invalidate()` — when the camera is still, frames stop and the
 * blades freeze. This keeps the demand-mode rendering loop honest
 * (per docs/notes/2026-04-11-demand-mode-viewers-must-gate-idle-
 * ambience-loops.md).
 */
export function SceneGrass({
	enabled,
	density,
	footprint,
	groundY,
	radius = DEFAULT_RADIUS_M,
	swayAmplitude = DEFAULT_SWAY_M,
}: SceneGrassProps) {
	const meshRef = useRef<THREE.InstancedMesh | null>(null);

	const geometry = useMemo(() => createBladeGeometry(), []);
	const { material, uniformsRef } = useMemo(() => createGrassMaterial(), []);

	useEffect(() => {
		const uniforms = uniformsRef.current;
		if (!uniforms) return;
		uniforms.uSway.value = swayAmplitude;
	}, [swayAmplitude, uniformsRef]);

	useEffect(
		() => () => {
			geometry.dispose();
		},
		[geometry],
	);
	useEffect(
		() => () => {
			material.dispose();
		},
		[material],
	);

	const placements = useMemo(
		() => (enabled ? placeInstances(density, radius, footprint) : []),
		[enabled, density, radius, footprint],
	);

	useEffect(() => {
		const mesh = meshRef.current;
		if (!mesh) return;
		const m = new THREE.Matrix4();
		const q = new THREE.Quaternion();
		const t = new THREE.Vector3();
		const s = new THREE.Vector3();
		const axis = new THREE.Vector3(0, 1, 0);
		for (let i = 0; i < placements.length; i += 1) {
			const p = placements[i];
			t.set(p.x, groundY, p.z);
			q.setFromAxisAngle(axis, p.rotation);
			s.setScalar(p.scale);
			m.compose(t, q, s);
			mesh.setMatrixAt(i, m);
		}
		mesh.count = placements.length;
		mesh.instanceMatrix.needsUpdate = true;
	}, [placements, groundY]);

	useFrame((state) => {
		const uniforms = uniformsRef.current;
		if (!uniforms) return;
		uniforms.uTime.value = state.clock.getElapsedTime();
	});

	if (!enabled || placements.length === 0) return null;

	return (
		<instancedMesh
			name="dressing-grass"
			ref={meshRef}
			args={[geometry, material, Math.max(1, placements.length)]}
			castShadow={false}
			receiveShadow
			frustumCulled={false}
		/>
	);
}
